import { useCallback, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { setSnackbar } from 'shared/store/slices/snackBarSlice';
import { describeResult, isSuccess, requestPackingList, stableStringify } from '../lib/packingListRequest';
import { allocationTargetKind, toAllocationRequest } from '../lib/packingListWire';
import { partyAliasSaved, uomAliasSaved } from '../store/packingListSlice';

/**
 * PL 저장 동작 모음. 화면 상태는 서버가 성공을 돌려준 뒤에만 바뀌고, 거절되면 이유를 띄운다.
 * 편집 중인 초안은 호출한 화면이 그대로 들고 있으므로 실패해도 입력이 사라지지 않는다.
 */
export default function usePackingListCommands(scope) {
    const dispatch = useDispatch();
    /** 확정 과정에서 마지막으로 임시저장에 성공한 내용. { id, version(저장 후), payloadKey } */
    const lastConfirmSave = useRef(null);

    /** quietCodes에 든 code의 거절(quiet면 모든 거절)은 스낵바를 띄우지 않는다. 호출한 화면이 직접 안내할 때 쓴다. */
    const send = useCallback(
        async (action, operation, body, { quietCodes = [], quiet = false } = {}) => {
            const result = await requestPackingList({ action, operation, scope, body });
            if (!isSuccess(result) && !quiet && !quietCodes.includes(result.code)) dispatch(setSnackbar({ open: true, severity: 'error', message: describeResult(result) }));
            return result;
        },
        [dispatch, scope]
    );

    return useMemo(
        () => ({
            /** 파일 없이 시작하는 초안. 서버가 문서를 만들고 id를 돌려준다. */
            createManualDocument: () => send('create', 'create_document', { input_source: 'manual' }),
            /** 검수 화면에 열 문서 한 건(편집본 포함). */
            readDocument: (id) => send('read', 'document', { id }),
            /**
             * 원본 뷰어(inline)·다운로드(attachment)용 단기 URL. 요청마다 서버가 소속·권한을 다시 확인하므로 만료되면 다시 부르면 된다.
             * 원본 없는 문서(수기)는 200 + NO_ORIGINAL_FILE + data null로 온다.
             */
            readFileUrl: ({ id, disposition = 'inline' }) => send('read', 'file_url', { id, disposition }),
            /** 문서 한 건의 작업 이력. 수정 사건은 전후 비교를 싣는다. */
            readHistory: ({ id, quiet = false }) => send('read', 'history', { id }, { quiet }),
            /** 올린 원본의 파싱 접수. 서버는 접수(202)만 답하고 결과는 packing_list:parsed 알림 뒤 read/document로 본다. */
            startParsing: ({ id, version, parseGeneration, quietCodes }) => send('update', 'start_parsing', { id, version, parse_generation: parseGeneration }, { quietCodes }),
            saveDraft: ({ id, version, draftPayload, quietCodes }) => send('update', 'save_draft', { id, version, draft_payload: draftPayload }, { quietCodes }),
            /**
             * 확정. 화면 최신본을 먼저 임시저장하고(확정이 거절돼도 편집본은 남는다) 올라간 version으로 확정한다.
             * 서버는 저장된 초안을 확정하므로 확정 요청에는 내용을 싣지 않는다 — version이 맞으면 그 초안이 곧 방금 저장한 내용이다.
             * savedVersion은 확정이 거절됐을 때 다음 요청에 쓸 version이다.
             * 직전 시도에서 같은 내용을 이미 이 version으로 저장했다면 임시저장을 건너뛴다. 확정 응답만 못 받은 뒤 다시 누르면
             * 확정 요청이 앞서와 똑같아져 같은 command_id로 나가므로, 이미 확정됐다면 서버가 다시 실행하지 않고 그 결과를 돌려준다.
             */
            confirm: async ({ id, version, allocationVersion, draftPayload }) => {
                const payloadKey = stableStringify(draftPayload);
                const last = lastConfirmSave.current;
                let savedVersion = version;
                if (!(last && last.id === id && last.version === version && last.payloadKey === payloadKey)) {
                    const saved = await send('update', 'save_draft', { id, version, draft_payload: draftPayload });
                    if (!isSuccess(saved)) return saved;
                    savedVersion = saved.data?.version ?? version;
                    lastConfirmSave.current = { id, version: savedVersion, payloadKey };
                }
                const confirmed = await send('update', 'confirm', { id, version: savedVersion, allocation_version: allocationVersion });
                return { ...confirmed, savedVersion };
            },
            /** 검수 중인 초안 폐기. */
            deleteDocument: ({ id, version, allocationVersion }) => send('delete', 'delete_document', { id, version, allocation_version: allocationVersion }),
            /**
             * 확정본 수정의 배분 영향 요약과 서버 검증 결과. 서버는 아무것도 저장하지 않는다.
             * allocationVersion은 수정을 연 시점의 배분 버전이다. 그 뒤 문서나 배분이 바뀌었으면 409로 거절한다.
             */
            readEditImpact: ({ id, version, allocationVersion, draftPayload, quietCodes }) => send('read', 'edit_impact', { id, version, allocation_version: allocationVersion, draft_payload: draftPayload }, { quietCodes }),
            /** 확정본 수정 재확정. 영향 요약에 동의한 뒤에만 보낸다. 서버가 다시 검증하고 통과해야 저장한다. 응답의 unallocated는 이번에 미배분이 된 라인 수다. */
            confirmEdit: ({ id, version, allocationVersion, draftPayload, quietCodes }) =>
                send('update', 'confirm_edit', { id, version, allocation_version: allocationVersion, draft_payload: draftPayload, impact_acknowledged: true }, { quietCodes }),
            /** 여러 품목의 배분을 한 번에 바꾼다. 전부 성공하거나 전부 거절된다. */
            saveAllocations: ({ allocationVersion, changes }) =>
                send('update', 'allocations', {
                    allocation_version: allocationVersion,
                    items: Object.entries(changes).map(([itemId, entries]) => toAllocationRequest(Number(itemId), entries, allocationTargetKind(scope.tab))),
                }),
            addUomAlias: async ({ raw, code }) => {
                const result = await send('create', 'add_uom_alias', { alias: raw, uom: code });
                if (isSuccess(result)) dispatch(uomAliasSaved({ raw, code }));
                return result;
            },
            addPartyAlias: async ({ tradePartnerId, alias }) => {
                const result = await send('create', 'add_party_alias', { il_trade_partner_id: tradePartnerId, alias });
                if (isSuccess(result)) dispatch(partyAliasSaved({ tradePartnerId, alias }));
                return result;
            },
        }),
        [send, dispatch, scope.tab]
    );
}
